/** 仿真主页（F-013/015/016/017 + M2: F-010/018/021 + F-022）。参考 v0 设计布局。 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Play, Pause, RotateCcw, StepForward, Download, RefreshCw, Info, Zap, ChevronDown } from 'lucide-react';
import { SimSession, type SimSessionState } from '../../session/sim-session';
import { buildExperimentConfig, buildStrategyFactory, useSetup } from '../store';
import { ExperimentConfigSchema } from '../../core/config-schema';
import { drawSimulation } from '../../render/composite';
import { findStrategy } from '../../strategy/registry';
import { createRuleStrategy } from '../../strategy/rule-engine/rule-strategy';
import { BUILTIN_STRATEGIES } from '../../strategy/registry';
import { LineChart, type Line } from '../components/LineChart';
import { ParamForm } from '../components/ParamForm';
import { resultsToCsv, type ExperimentResult } from '../../data/export-csv';
import { experimentToJson, type ExperimentJson } from '../../data/export-json';
import { downloadText } from '../../data/download';
import type { MetricsSummary } from '../../core/metrics/collector';
import type { StrategyParams } from '../../strategy/strategy-api';
import { THEME } from '../../render/theme';
import { cn } from '../cn';

const SPEEDS = [0.5, 1, 2, 4, 8, 16, 32];

const METRIC_DEFS: { key: keyof MetricsSummary; label: string; unit: string; desc: string; color: string; fmt: (v: number) => string }[] = [
  { key: 'deliveredCount', label: '已送达', unit: '人', desc: '已成功送达目标楼层的乘客总数。', color: 'text-chart-3', fmt: (v) => Math.round(v).toLocaleString() },
  { key: 'throughputPerHour', label: '吞吐量', unit: '/h', desc: '送达总数 / 仿真已运行时长(h)。', color: 'text-primary', fmt: (v) => v.toFixed(0) },
  { key: 'avgWaitMs', label: '平均等待', unit: 's', desc: '从按呼梯到电梯开门的平均时间，越低越好。', color: 'text-primary', fmt: (v) => (v / 1000).toFixed(1) },
  { key: 'maxWaitMs', label: '最长等待', unit: 's', desc: '所有乘客等待时间的最大值。', color: 'text-destructive', fmt: (v) => (v / 1000).toFixed(1) },
  { key: 'avgRideMs', label: '平均乘梯', unit: 's', desc: '进入轿厢到到达目标楼层的平均时长。', color: 'text-foreground', fmt: (v) => (v / 1000).toFixed(1) },
  { key: 'carUtilization', label: '利用率', unit: '%', desc: 'Σ(载客×运行)/(容量×总运行)。', color: 'text-chart-2', fmt: (v) => (v * 100).toFixed(1) },
  { key: 'waitingCount', label: '候梯中', unit: '人', desc: '当前各楼层等候电梯的乘客总数。', color: 'text-foreground', fmt: (v) => Math.round(v).toString() },
  { key: 'energyKwh', label: '能耗估算', unit: 'kWh', desc: '基于运行距离与载重的估算值，仅供参考。', color: 'text-chart-4', fmt: (v) => v.toFixed(2) },
];

function MetricCard({ def, value }: { def: (typeof METRIC_DEFS)[number]; value: number }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative group flex flex-col gap-1 p-3 rounded-lg bg-secondary/40 border border-border/60">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{def.label}</span>
        <button onClick={() => setShow(!show)} className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground" aria-label={`查看 ${def.label} 说明`}>
          <Info className="w-3 h-3" />
        </button>
      </div>
      <span className={cn('text-xl font-mono num font-semibold', def.color)}>
        {def.fmt(value)}<span className="text-xs text-muted-foreground ml-1">{def.unit}</span>
      </span>
      {show && (
        <div className="absolute z-20 top-full left-0 right-0 mt-1 p-2 rounded border border-border bg-popover text-[11px] text-muted-foreground leading-relaxed shadow-xl">{def.desc}</div>
      )}
    </div>
  );
}

function fmtClock(ms: number): string {
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export function SimulationPage() {
  const boot = useMemo(() => {
    const st = useSetup.getState();
    return {
      config: ExperimentConfigSchema.parse(buildExperimentConfig(st)),
      factory: buildStrategyFactory(st),
      floors: st.floors,
      elevCount: st.elevatorCount,
      strategyId: st.strategyId,
      initParams: { ...st.strategyParams } as StrategyParams,
      rules: st.rules,
      seed: st.seed,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sessionRef = useRef<SimSession | null>(null);
  const [runState, setRunState] = useState<SimSessionState>('idle');
  const [speed, setSpeedState] = useState(1);
  const [strategyId, setStrategyId] = useState(boot.strategyId);
  const [params, setParams] = useState<StrategyParams>(boot.initParams);
  const [metrics, setMetrics] = useState<MetricsSummary | null>(null);
  const [simTimeMs, setSimTimeMs] = useState(0);
  const [lines, setLines] = useState<Line[]>([]);

  const paramFields = useMemo(
    () => (strategyId === 'rule-engine' ? undefined : findStrategy(strategyId)?.make().params),
    [strategyId],
  );

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = Math.floor(canvas.clientWidth * dpr);
      canvas.height = Math.floor(canvas.clientHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    window.addEventListener('resize', resize);
    const session = new SimSession(boot.config, boot.factory, {
      onFrame: (snap, visMs) => drawSimulation(ctx, snap, visMs, { width: canvas.clientWidth, height: canvas.clientHeight, floors: snap.floors.length, kin: session.kinematics, waitWarnMs: 90_000 }),
      onMetrics: (m) => {
        setMetrics(m);
        setSimTimeMs(session.snapshot.simTimeMs);
        const series = session.metricsSeries;
        setLines([
          { label: '平均等待(s)', color: THEME.chart1, data: series.map((p) => [p.tMs, p.avgWaitMs / 1000]) },
          { label: '候梯人数', color: THEME.chart2, data: series.map((p) => [p.tMs, p.waiting]) },
        ]);
      },
      onStateChange: setRunState,
    });
    sessionRef.current = session;
    session.reset();
    return () => { session.terminate(); window.removeEventListener('resize', resize); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSpeed = (sp: number) => { setSpeedState(sp); sessionRef.current?.setSpeed(sp); };
  const onSwitchStrategy = (id: string) => {
    const factory = () => (id === 'rule-engine' ? createRuleStrategy(boot.rules) : findStrategy(id)!.make());
    sessionRef.current?.switchStrategy(factory);
    setStrategyId(id);
    setParams({});
  };
  const onExportCsv = () => {
    if (!metrics) return;
    const row: ExperimentResult = { id: 'single', label: '单次实验', sceneId: 'custom', strategyId, seed: boot.seed, summary: metrics, params, ok: true };
    downloadText('metrics.csv', resultsToCsv([row]), 'text/csv');
  };
  const onExportJson = () => {
    const session = sessionRef.current;
    if (!session || !metrics) return;
    const payload: ExperimentJson = { config: session.config, strategyId, params, summary: metrics, eventLog: session.eventLog, metaLog: session.metaLog, metricsSeries: session.metricsSeries };
    downloadText('experiment.json', experimentToJson(payload), 'application/json');
  };

  const running = runState === 'running';
  const statusLabel: Record<SimSessionState, string> = { idle: '待机', running: '运行中', paused: '已暂停', finished: '已结束' };
  const statusColor = runState === 'running' ? 'text-chart-3' : runState === 'paused' ? 'text-accent' : 'text-muted-foreground';

  return (
    <div className="flex flex-col gap-4">
      {/* 控制条 */}
      <div className="flex items-center gap-3 flex-wrap p-4 rounded-lg border border-border bg-card">
        <div className="flex items-center gap-2">
          {runState === 'idle' || runState === 'paused' ? (
            <button onClick={() => (runState === 'idle' ? sessionRef.current?.start() : sessionRef.current?.resume())} className="flex items-center gap-1.5 h-8 px-4 rounded text-sm font-semibold bg-primary text-primary-foreground hover:bg-primary/85 transition-colors">
              <Play className="w-3.5 h-3.5 fill-current" />{runState === 'idle' ? '开始' : '继续'}
            </button>
          ) : (
            <button onClick={() => sessionRef.current?.pause()} className="flex items-center gap-1.5 h-8 px-4 rounded text-sm font-medium bg-secondary border border-border text-foreground hover:bg-secondary/70 transition-colors">
              <Pause className="w-3.5 h-3.5" />暂停
            </button>
          )}
          <button onClick={() => sessionRef.current?.step()} disabled={running || runState === 'finished'} title="单步" className="flex items-center justify-center h-8 w-8 rounded text-sm bg-secondary border border-border text-foreground hover:bg-secondary/70 disabled:opacity-40 transition-colors">
            <StepForward className="w-3.5 h-3.5" />
          </button>
          <button onClick={() => sessionRef.current?.reset()} title="重置" className="flex items-center justify-center h-8 w-8 rounded text-sm bg-secondary border border-border text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors">
            <RotateCcw className="w-3.5 h-3.5" />
          </button>
        </div>

        <div className="flex items-center gap-1 ml-auto">
          <span className="text-[11px] font-mono text-muted-foreground mr-1">倍速</span>
          {SPEEDS.map((sp) => (
            <button key={sp} onClick={() => onSpeed(sp)} className={cn('h-7 px-2 text-xs font-mono rounded transition-colors', speed === sp ? 'bg-primary text-primary-foreground' : 'bg-secondary border border-border text-muted-foreground hover:text-foreground')}>{sp}×</button>
          ))}
        </div>

        <div className="flex items-center gap-2 px-3 py-1 rounded border border-border bg-secondary/40">
          <span className={cn('w-2 h-2 rounded-full', running ? 'bg-chart-3 animate-pulse' : 'bg-muted')} />
          <span className={cn('text-xs font-mono', statusColor)}>{statusLabel[runState]}</span>
          <span data-testid="sim-clock" className="text-xs font-mono text-muted-foreground num ml-1">T={fmtClock(simTimeMs)}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
        {/* 左：画布 + 时序 */}
        <div className="flex flex-col gap-4 min-w-0">
          <div className="rounded-lg border border-border bg-card p-4 overflow-auto">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-foreground">井道视图 · {boot.floors}F · {boot.elevCount}台</span>
              <span className="text-[10px] font-mono text-muted-foreground">青=上行呼叫 · 琥珀=下行呼叫 · 红=超时</span>
            </div>
            <canvas ref={canvasRef} className="w-full h-[60vh] rounded bg-[oklch(0.105_0.008_240)]" />
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold text-foreground mb-3">指标时间序列（F-018）</p>
            <LineChart lines={lines} yLabel="值" />
          </div>
        </div>

        {/* 右：侧栏 */}
        <div className="flex flex-col gap-3">
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="text-xs font-semibold text-foreground mb-3">实时指标（F-017）</p>
            {metrics ? (
              <div className="grid grid-cols-2 gap-2">
                {METRIC_DEFS.map((d) => <MetricCard key={d.key} def={d} value={metrics[d.key]} />)}
              </div>
            ) : <p className="text-xs text-muted-foreground">等待启动…</p>}
          </div>

          <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-3">
            <div className="flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-primary" /><p className="text-xs font-semibold text-foreground">策略热切换（F-010）</p></div>
            <div className="relative">
              <select data-testid="hotswap-select" value={strategyId} onChange={(e) => onSwitchStrategy(e.target.value)} className="w-full h-8 bg-input/40 border border-border rounded px-2.5 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary/60 appearance-none pr-8">
                {BUILTIN_STRATEGIES.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                <option value="rule-engine">自定义规则引擎</option>
              </select>
              <ChevronDown className="absolute right-2 top-2 w-4 h-4 text-muted-foreground pointer-events-none" />
            </div>
            {paramFields && paramFields.length > 0 && (
              <details className="text-sm">
                <summary className="text-muted-foreground cursor-pointer text-xs">策略参数（F-009，实时生效）</summary>
                <div className="mt-2"><ParamForm fields={paramFields} value={params} onChange={(p) => { setParams(p); sessionRef.current?.updateParams(p); }} /></div>
              </details>
            )}
          </div>

          <div className="rounded-lg border border-border bg-card p-4 flex flex-col gap-2">
            <p className="text-xs font-semibold text-foreground mb-1">导出（F-021）</p>
            <button onClick={onExportCsv} disabled={!metrics} className="flex items-center gap-2 h-7 px-3 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 disabled:opacity-40 transition-colors"><Download className="w-3.5 h-3.5" />导出指标 CSV</button>
            <button onClick={onExportJson} disabled={!metrics} className="flex items-center gap-2 h-7 px-3 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 disabled:opacity-40 transition-colors"><Download className="w-3.5 h-3.5" />导出事件日志 JSON</button>
            <button onClick={() => { sessionRef.current?.reset(); sessionRef.current?.start(); }} className="flex items-center gap-2 h-7 px-3 text-xs rounded border border-border text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors"><RefreshCw className="w-3.5 h-3.5" />同种子重跑（F-022）</button>
          </div>
        </div>
      </div>
    </div>
  );
}
